import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tempGuestAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import {
  Shield,
  Loader2,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  MessageCircle,
  Clock,
  Mail,
} from 'lucide-react';

const SUBJECT_TYPES = [
  { value: 'employees', label: 'Employees' },
  { value: 'customers', label: 'Customers' },
  { value: 'applicants', label: 'Job Applicants' },
  { value: 'public', label: 'Members of the Public' },
  { value: 'children', label: 'Children/Minors' },
  { value: 'vulnerable', label: 'Vulnerable Persons' },
];

const DATA_TYPES = [
  { value: 'identification', label: 'Identification Data' },
  { value: 'contact', label: 'Contact Information' },
  { value: 'financial', label: 'Financial Data' },
  { value: 'health', label: 'Health Data' },
  { value: 'biometric', label: 'Biometric Data' },
  { value: 'genetic', label: 'Genetic Data' },
  { value: 'location', label: 'Location Data' },
  { value: 'online_identifiers', label: 'Online Identifiers' },
];

const VOLUME_OPTIONS = [
  { value: 'small', label: 'Small (< 100 subjects)' },
  { value: 'medium', label: 'Medium (100 - 1,000)' },
  { value: 'large', label: 'Large (1,000 - 10,000)' },
  { value: 'very_large', label: 'Very Large (> 10,000)' },
];

const OUTCOME_COLORS = {
  DPIA_REQUIRED: {
    icon: AlertCircle,
    badge: 'destructive',
    bgClass: 'bg-destructive/10 border-destructive/30',
    textClass: 'text-destructive',
    accentBg: 'bg-destructive/5',
  },
  DPIA_RECOMMENDED: {
    icon: AlertTriangle,
    badge: 'warning',
    bgClass: 'bg-yellow-500/10 border-yellow-500/30',
    textClass: 'text-yellow-700 dark:text-yellow-500',
    accentBg: 'bg-yellow-500/5',
  },
  NO_REVIEW_REQUIRED: {
    icon: CheckCircle2,
    badge: 'success',
    bgClass: 'bg-green-500/10 border-green-500/30',
    textClass: 'text-green-700 dark:text-green-500',
    accentBg: 'bg-green-500/5',
  },
  SPEAK_TO_DPO: {
    icon: MessageCircle,
    badge: 'secondary',
    bgClass: 'bg-blue-500/10 border-blue-500/30',
    textClass: 'text-blue-700 dark:text-blue-500',
    accentBg: 'bg-blue-500/5',
  },
};

export default function PreScreenFormPage({ guestMode = false }) {
  const navigate = useNavigate();
  const guestUser = guestMode ? JSON.parse(localStorage.getItem('guest_user') || '{}') : null;

  const [step, setStep] = useState('form'); // 'form' | 'result'
  const [outcome, setOutcome] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    submitter_name: guestUser?.full_name || '',
    submitter_email: guestUser?.email || '',
    submitter_title: '',
    submitter_department: '',
    submitter_phone: '',
    q1_processes_personal_data: null,
    q2_processing_necessary: 'not_sure',
    q3_subject_types: [],
    q4_data_types: [],
    q5_volume: 'small',
    q6_external_sharing: false,
    q6_external_description: '',
    q7_monitoring: false,
    q7_monitoring_description: '',
    q8_ai_systems: false,
    q8_ai_description: '',
  });

  const [errors, setErrors] = useState({});

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const toggleArrayField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value],
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.submitter_name.trim()) newErrors.submitter_name = 'Name is required';
    if (!formData.submitter_email.trim()) newErrors.submitter_email = 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.submitter_email)) {
      newErrors.submitter_email = 'Please enter a valid email';
    }
    if (formData.q1_processes_personal_data === null) newErrors.q1 = 'This question is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSubmitting(true);
    try {
      await tempGuestAPI.submitPrescreen({ ...formData, project_name: 'Pre-Screen Assessment' });
      toast.success('Assessment submitted successfully!');
      navigate('/guest/portal');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Please try again.');
      console.error('Submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'result') {
    const outcomeConfig = OUTCOME_COLORS[outcome.outcome] || OUTCOME_COLORS.DPIA_RECOMMENDED;
    const OutcomeIcon = outcomeConfig.icon;

    return (
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 dark:from-slate-950 dark:to-slate-900 text-white py-8 px-6 border-b border-primary/20">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-8 h-8" />
              <h1 className="text-3xl font-bold">Privacy Impact Pre-Screen</h1>
            </div>
            <p className="text-slate-300">Assessment Result</p>
          </div>
        </div>

        {/* Result Panel */}
        <div className="max-w-2xl mx-auto p-6">
          <Card className="shadow-lg border border-primary/10">
            <CardContent className={`pt-12 pb-12 text-center ${outcomeConfig.bgClass} border rounded-lg`}>
              <div className={`flex justify-center mb-6`}>
                <OutcomeIcon className={`w-16 h-16 ${outcomeConfig.textClass}`} />
              </div>

              <h2 className="text-2xl font-bold mb-2 text-foreground">
                {outcome.outcome.replace(/_/g, ' ')}
              </h2>

              <div className="flex justify-center gap-2 mb-6">
                <Badge variant={outcomeConfig.badge} className="text-base px-3 py-1">
                  {outcome.prescreen_reference}
                </Badge>
              </div>

              <p className="text-lg text-foreground/80 mb-6 max-w-xl mx-auto">
                {outcome.outcome_message}
              </p>

              <div className={`${outcomeConfig.accentBg} border border-primary/20 rounded-lg p-4 text-left mb-6`}>
                <p className="text-sm font-semibold text-foreground mb-2">Assessment Reason:</p>
                <p className="text-sm text-muted-foreground">{outcome.outcome_rationale}</p>
              </div>

              {outcome.outcome === 'DPIA_REQUIRED' && (
                <div className="border border-primary/20 rounded-lg p-4 text-left mb-6 bg-primary/5">
                  <p className="text-sm font-semibold text-foreground mb-1">Next Steps</p>
                  <p className="text-sm text-muted-foreground">
                    A draft Data Protection Impact Assessment (DPIA-{outcome.intake_reference}) has been automatically created. Your Data Protection Officer will contact you to complete the full assessment.
                  </p>
                </div>
              )}

              <div className="flex items-center justify-center gap-2 mb-6 text-sm">
                <Mail className="w-4 h-4 text-primary" />
                <span className="text-muted-foreground">
                  A copy of this result has been sent to <span className="font-semibold text-foreground">{outcome.submitter_email}</span>
                </span>
              </div>

              <p className="text-xs text-muted-foreground">
                You can close this page. Your assessment has been recorded.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Form screen
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 dark:from-slate-950 dark:to-slate-900 text-white py-8 px-6 border-b border-primary/20 shadow-sm">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8" />
            <h1 className="text-3xl font-bold">Privacy Impact Pre-Screen</h1>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <p className="text-sm opacity-90">Takes approximately 2 minutes to complete</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="shadow-lg border border-primary/10">
            <CardContent className="p-8">
              {/* Contact Section */}
              <div className="space-y-6 mb-8">
                <div>
                  <h3 className="font-semibold text-lg text-foreground mb-1">Contact Information</h3>
                  <p className="text-sm text-muted-foreground mb-4">How we can reach you with the assessment result</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-foreground">Full Name *</Label>
                    <Input
                      value={formData.submitter_name}
                      onChange={(e) => updateField('submitter_name', e.target.value)}
                      placeholder="Your full name"
                      className={`bg-background border-input text-foreground placeholder:text-muted-foreground ${
                        errors.submitter_name ? 'border-destructive focus-visible:ring-destructive' : ''
                      }`}
                    />
                    {errors.submitter_name && (
                      <p className="text-xs text-destructive flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.submitter_name}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-foreground">Email Address *</Label>
                    <Input
                      type="email"
                      value={formData.submitter_email}
                      onChange={(e) => updateField('submitter_email', e.target.value)}
                      placeholder="your.email@example.com"
                      className={`bg-background border-input text-foreground placeholder:text-muted-foreground ${
                        errors.submitter_email ? 'border-destructive focus-visible:ring-destructive' : ''
                      }`}
                    />
                    {errors.submitter_email && (
                      <p className="text-xs text-destructive flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.submitter_email}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-foreground">Job Title</Label>
                    <Input
                      value={formData.submitter_title}
                      onChange={(e) => updateField('submitter_title', e.target.value)}
                      placeholder="e.g., Product Manager"
                      className="bg-background border-input text-foreground placeholder:text-muted-foreground"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-foreground">Department</Label>
                    <Input
                      value={formData.submitter_department}
                      onChange={(e) => updateField('submitter_department', e.target.value)}
                      placeholder="e.g., Engineering"
                      className="bg-background border-input text-foreground placeholder:text-muted-foreground"
                    />
                  </div>
                </div>
              </div>

              <Separator className="my-8 bg-border" />

              {/* Assessment Questions */}
              <div className="space-y-8">
                <div>
                  <h3 className="font-semibold text-lg text-foreground mb-1">Assessment Questions</h3>
                  <p className="text-sm text-muted-foreground mb-4">Please answer these 8 questions truthfully</p>
                </div>

                {/* Q1 */}
                <div className="space-y-3">
                  <Label className="text-base font-medium text-foreground">
                    Q1: Does your project involve processing personal data? *
                  </Label>
                  <div className="flex gap-3">
                    <Button
                      variant={formData.q1_processes_personal_data === true ? 'default' : 'outline'}
                      onClick={() => updateField('q1_processes_personal_data', true)}
                      className="font-medium"
                    >
                      Yes
                    </Button>
                    <Button
                      variant={formData.q1_processes_personal_data === false ? 'default' : 'outline'}
                      onClick={() => updateField('q1_processes_personal_data', false)}
                      className="font-medium"
                    >
                      No
                    </Button>
                  </div>
                  {errors.q1 && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.q1}
                    </p>
                  )}
                </div>

                {/* Q2 */}
                <div className="space-y-3">
                  <Label className="text-base font-medium text-foreground">
                    Q2: Is processing this data necessary for your project objectives?
                  </Label>
                  <div className="flex gap-3 flex-wrap">
                    {['yes', 'no', 'not_sure'].map(opt => (
                      <Button
                        key={opt}
                        variant={formData.q2_processing_necessary === opt ? 'default' : 'outline'}
                        onClick={() => updateField('q2_processing_necessary', opt)}
                        className="capitalize font-medium"
                      >
                        {opt.replace('_', ' ')}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Q3 */}
                <div className="space-y-3">
                  <Label className="text-base font-medium text-foreground">Q3: What types of data subjects are affected?</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 rounded-lg bg-muted/50 border border-border">
                    {SUBJECT_TYPES.map(type => (
                      <div key={type.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={`q3_${type.value}`}
                          checked={formData.q3_subject_types.includes(type.value)}
                          onCheckedChange={() => toggleArrayField('q3_subject_types', type.value)}
                        />
                        <Label
                          htmlFor={`q3_${type.value}`}
                          className="font-normal cursor-pointer text-foreground"
                        >
                          {type.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Q4 */}
                <div className="space-y-3">
                  <Label className="text-base font-medium text-foreground">Q4: Categories of personal data processed</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 rounded-lg bg-muted/50 border border-border">
                    {DATA_TYPES.map(type => (
                      <div key={type.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={`q4_${type.value}`}
                          checked={formData.q4_data_types.includes(type.value)}
                          onCheckedChange={() => toggleArrayField('q4_data_types', type.value)}
                        />
                        <Label
                          htmlFor={`q4_${type.value}`}
                          className="font-normal cursor-pointer text-foreground"
                        >
                          {type.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Q5 */}
                <div className="space-y-3">
                  <Label className="text-base font-medium text-foreground">Q5: Estimated volume of data subjects</Label>
                  <div className="flex gap-3 flex-wrap">
                    {VOLUME_OPTIONS.map(opt => (
                      <Button
                        key={opt.value}
                        variant={formData.q5_volume === opt.value ? 'default' : 'outline'}
                        onClick={() => updateField('q5_volume', opt.value)}
                        className="text-sm font-medium"
                      >
                        {opt.label}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Q6 */}
                <div className="space-y-3 p-4 bg-muted/50 rounded-lg border border-border">
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="q6"
                      checked={formData.q6_external_sharing}
                      onCheckedChange={(v) => updateField('q6_external_sharing', v)}
                      className="mt-1"
                    />
                    <Label htmlFor="q6" className="font-medium cursor-pointer text-base text-foreground">
                      Q6: Will data be shared with external organizations?
                    </Label>
                  </div>
                  {formData.q6_external_sharing && (
                    <Textarea
                      placeholder="Describe the external organizations and purposes..."
                      value={formData.q6_external_description}
                      onChange={(e) => updateField('q6_external_description', e.target.value)}
                      rows={3}
                      className="bg-background border-input text-foreground placeholder:text-muted-foreground"
                    />
                  )}
                </div>

                {/* Q7 */}
                <div className="space-y-3 p-4 bg-muted/50 rounded-lg border border-border">
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="q7"
                      checked={formData.q7_monitoring}
                      onCheckedChange={(v) => updateField('q7_monitoring', v)}
                      className="mt-1"
                    />
                    <Label htmlFor="q7" className="font-medium cursor-pointer text-base text-foreground">
                      Q7: Will the project involve systematic monitoring, tracking, or surveillance?
                    </Label>
                  </div>
                  {formData.q7_monitoring && (
                    <Textarea
                      placeholder="Describe the monitoring or tracking activities..."
                      value={formData.q7_monitoring_description}
                      onChange={(e) => updateField('q7_monitoring_description', e.target.value)}
                      rows={3}
                      className="bg-background border-input text-foreground placeholder:text-muted-foreground"
                    />
                  )}
                </div>

                {/* Q8 */}
                <div className="space-y-3 p-4 bg-muted/50 rounded-lg border border-border">
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="q8"
                      checked={formData.q8_ai_systems}
                      onCheckedChange={(v) => updateField('q8_ai_systems', v)}
                      className="mt-1"
                    />
                    <Label htmlFor="q8" className="font-medium cursor-pointer text-base text-foreground">
                      Q8: Will the project use AI or automated decision-making?
                    </Label>
                  </div>
                  {formData.q8_ai_systems && (
                    <Textarea
                      placeholder="Describe the AI systems or automated decision-making..."
                      value={formData.q8_ai_description}
                      onChange={(e) => updateField('q8_ai_description', e.target.value)}
                      rows={3}
                      className="bg-background border-input text-foreground placeholder:text-muted-foreground"
                    />
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="mt-8 flex gap-3">
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  size="lg"
                  className="flex-1 font-medium"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Assessment'
                  )}
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center mt-4">
                Your information is secure and will be processed confidentially.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
